<div class="library-tree-container">
	<table
		class="library-tree-table"
		role="treegrid"
		[attr.aria-colcount]="getTreeAriaColCount()"
		[attr.aria-multiselectable]="getTreeAriaMultiselectable()"
		[attr.aria-label]="'library-tree-viewer_list-label' | translate">
		<colgroup>
			@if (multiSelect) {
				<col class="library-tree-col library-tree-col-select">
			}
			<col class="library-tree-col library-tree-col-name">
			<col class="library-tree-col library-tree-col-modified">
			<col class="library-tree-col library-tree-col-actions">
		</colgroup>
		<thead>
			<tr role="row" class="library-tree-header-row">
				@if (multiSelect) {
					<th
						[id]="getTreeGridHeaderId('select')"
						scope="col"
						role="columnheader"
						class="library-tree-header library-tree-header-select">
						<span class="accessibility-text">{{ 'common_selected' | translate }}</span>
						@if (!searchStr) {
							<mat-checkbox
								class="select-all"
								color="primary"
								[checked]="selectAllChecked"
								(change)="toggleSelectAll.emit($event.checked)"
								[attr.aria-label]="'library-collections_multi-select-all' | translate">
							</mat-checkbox>
						}
					</th>
				}
				<th
					[id]="getTreeGridHeaderId('name')"
					scope="col"
					role="columnheader"
					class="library-tree-header library-tree-header-name">
					<div class="library-tree-header-name-content">
						<span>{{ 'library-collections_header-name' | translate }}</span>
					</div>
				</th>
				<th
					[id]="getTreeGridHeaderId('modified')"
					scope="col"
					role="columnheader"
					class="library-tree-header library-tree-header-modified">
					<span>{{ 'library-collections_header-modified' | translate }}</span>
				</th>
				<th
					[id]="getTreeGridHeaderId('actions')"
					scope="col"
					role="columnheader"
					class="library-tree-header library-tree-header-actions">
					<div class="library-tree-header-actions-content">
						<span class="accessibility-text">{{ 'common_actions' | translate }}</span>
						@if (multiSelect) {
							<button
								type="button"
								[matMenuTriggerFor]="bulkActionsMenu"
								class="menu-btn library-tree-bulk-actions-button item-options-dropdown-menu btn-none"
								data-cy="library-tree-bulk-actions-menu"
								[attr.aria-label]="'common_actions' | translate">
								<i class="ficon-app-dots-vertical"></i>
							</button>
						}
					</div>
				</th>
			</tr>
		</thead>
		<tbody>
			@for (node of dataSource.data; track trackNode($index, node)) {
				@if (node.type === 'folder') {
					<tr
						ngxCollectionFolderRow
						role="row"
						class="library-tree-row"
						[id]="getNodeElementId(node)"
						[attr.data-tree-node-key]="node.key"
						[attr.aria-label]="getNodeAriaLabel(node)"
						[attr.aria-selected]="getNodeAriaSelected(node)"
						[attr.aria-checked]="getNodeAriaChecked(node)"
						[attr.aria-expanded]="getNodeAriaExpanded(node)"
						[attr.aria-level]="node.level + 1"
						[attr.aria-posinset]="getNodeAriaPosInSet(node)"
						[attr.aria-setsize]="getNodeAriaSetSize(node)"
						[attr.tabindex]="getRowTabindex(node)"
						[style.--node-level]="node.level"
						(focus)="handleRowFocus(node.key)"
						(keydown)="handleRowKeydown($event, node)"
						[nodeLevel]="node.level"
						[folder]="getFolderData(node)"
						[mode]="mode"
						[collections]="collections"
						[archiveCollection]="archiveCollection"
						[selectedCollection]="selectedCollection"
						[selectedContentType]="selectedContentType"
						[orgSettings]="orgSettings"
						[sortBy]="sortBy"
						[searchStr]="searchStr"
						[multiSelect]="multiSelect"
						[expandedHeight]="expandedHeight"
						[currentlySelected]="currentlySelected"
						(selectItem)="handleSelectItem($event)"
						(setActivityPanelIsOpen)="setActivityPanelIsOpen.emit($event)"
						(setCurrentlySelected)="setCurrentlySelected.emit($event)"
						(setSelectAllChecked)="setSelectAllChecked.emit($event)"
						(treeGridFocus)="handleCellFocus(node.key, $event)"
						(treeGridKeydown)="handleCellKeydown($event.event, node, $event.target)"
						[treeGridSelectHeaderId]="getTreeGridHeaderId('select')"
						[treeGridNameHeaderId]="getTreeGridHeaderId('name')"
						[treeGridModifiedHeaderId]="getTreeGridHeaderId('modified')"
						[treeGridNameTabindex]="getCellTabindex(node, 'name')"
						[treeGridModifiedTabindex]="getCellTabindex(node, 'modified')"
						[treeGridActionTabindex]="getActionTabindex(node)">
					</tr>

					@if (node.isLoading) {
						<tr role="row" class="library-tree-status-row">
							<td role="gridcell" class="library-tree-status-cell" [attr.colspan]="getTreeColspan()">
								<mat-progress-bar [attr.aria-label]="'common_loading'| translate" mode="indeterminate"></mat-progress-bar>
							</td>
						</tr>
					}

					@if (isFolderEmpty(node)) {
						<tr role="row" class="library-tree-status-row">
							<td role="gridcell" class="library-tree-status-cell" [attr.colspan]="getTreeColspan()">
								<div class="tree-folder-empty-state">
									<span>{{'collection-folder-no-items' | translate}}</span>
								</div>
							</td>
						</tr>
					}
				} @else if (node.type === 'item') {
					<tr
						ngxCollectionItemRow
						role="row"
						class="library-tree-row"
						[id]="getNodeElementId(node)"
						[attr.data-tree-node-key]="node.key"
						[attr.aria-label]="getNodeAriaLabel(node)"
						[attr.aria-selected]="getNodeAriaSelected(node)"
						[attr.aria-checked]="getNodeAriaChecked(node)"
						[attr.aria-level]="node.level + 1"
						[attr.aria-posinset]="getNodeAriaPosInSet(node)"
						[attr.aria-setsize]="getNodeAriaSetSize(node)"
						[attr.tabindex]="getRowTabindex(node)"
						[style.--node-level]="node.level"
						(focus)="handleRowFocus(node.key)"
						(keydown)="handleRowKeydown($event, node)"
						[node]="getItemNode(node)"
						[nodeLevel]="node.level"
						[mode]="mode"
						[collections]="collections"
						[orgSettings]="orgSettings"
						[archiveCollection]="archiveCollection"
						[selectedCollection]="selectedCollection"
						[selectedContentType]="selectedContentType"
						[multiSelect]="multiSelect"
						[expandedHeight]="expandedHeight"
						[currentlySelected]="currentlySelected"
						(setActivityPanelIsOpen)="setActivityPanelIsOpen.emit($event)"
						(setCurrentlySelected)="setCurrentlySelected.emit($event)"
						(selectItem)="handleSelectItem($event)"
						(loadNewItems)="loadNewItems.emit($event)"
						(setSelectAllChecked)="setSelectAllChecked.emit($event)"
						(treeGridFocus)="handleCellFocus(node.key, $event)"
						(treeGridKeydown)="handleCellKeydown($event.event, node, $event.target)"
						[treeGridSelectHeaderId]="getTreeGridHeaderId('select')"
						[treeGridNameHeaderId]="getTreeGridHeaderId('name')"
						[treeGridModifiedHeaderId]="getTreeGridHeaderId('modified')"
						[treeGridNameTabindex]="getCellTabindex(node, 'name')"
						[treeGridModifiedTabindex]="getCellTabindex(node, 'modified')"
						[treeGridActionTabindex]="getActionTabindex(node)"
						[treeGridCheckboxTabindex]="getCheckboxTabindex(node)">
					</tr>

					@if (isItemDetailsLoading(getItemHash(node))) {
						<tr role="row" class="library-tree-status-row">
							<td role="gridcell" class="library-tree-status-cell" [attr.colspan]="getTreeColspan()">
								<mat-progress-bar [attr.aria-label]="'common_loading'| translate" mode="indeterminate"></mat-progress-bar>
							</td>
						</tr>
					}

					@if (dataSource.isItemExpanded(getItemHash(node)) && !isItemDetailsLoading(getItemHash(node))) {
						<tr
							role="row"
							class="library-tree-detail-row"
							[class.active]="isNodeCurrentlySelected(node)">
							<td role="gridcell" class="library-tree-detail-cell" [attr.colspan]="getTreeColspan()">
								<ngx-collection-item-viewer
									[treeTableDetailView]="true"
									[style.--node-level]="node.level"
									[node]="getItemNode(node)"
									[nodeLevel]="node.level"
									[mode]="mode"
									[collections]="collections"
									[orgSettings]="orgSettings"
									[archiveCollection]="archiveCollection"
									[selectedCollection]="selectedCollection"
									[selectedContentType]="selectedContentType"
									[multiSelect]="multiSelect"
									[expandedHeight]="expandedHeight"
									[currentlySelected]="currentlySelected"
									(setActivityPanelIsOpen)="setActivityPanelIsOpen.emit($event)"
									(setCurrentlySelected)="setCurrentlySelected.emit($event)"
									(selectItem)="handleSelectItem($event)"
									(loadNewItems)="loadNewItems.emit($event)"
									(setSelectAllChecked)="setSelectAllChecked.emit($event)">
								</ngx-collection-item-viewer>
							</td>
						</tr>
					}
				}
			}
		</tbody>
		</table>

		<mat-menu #bulkActionsMenu="matMenu">
			<button
				mat-menu-item
				type="button"
				class="copy-option"
				data-cy="common-copy"
				(click)="copyCheckedItems.emit()">
				<span>{{ 'common_copy' | translate }}</span>
			</button>
			@if (!hideOnArchiveAndManaged) {
				<button
					mat-menu-item
					type="button"
					class="move-option"
					data-cy="common-move"
					(click)="moveCheckedItems.emit()">
					<span>{{ 'common_move' | translate }}</span>
				</button>
				<button
					mat-menu-item
					type="button"
					class="remove-option"
					data-cy="common-remove"
					(click)="removeCheckedItems.emit()">
					<span>{{ 'common_remove' | translate }}</span>
				</button>
			}
		</mat-menu>
	</div>
